import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map, skip } from 'rxjs/operators';

import { Colors as DarkTheme } from './../theme/dark/ts/colors';
import { Colors as LightTheme } from './../theme/light/ts/colors';

export const SupportedThemes = { light: LightTheme, dark: DarkTheme };

export type SupportedThemesT = keyof typeof SupportedThemes;
export type Theme = (typeof SupportedThemes)[SupportedThemesT];

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public static readonly DEFAULT_THEME: SupportedThemesT = 'light';
  private static readonly LS_KEY: string = 'recruitee-theme';

  public activeTheme$: Observable<Theme>;
  private supportedThemes$: BehaviorSubject<SupportedThemesT> = new BehaviorSubject(
    this.retrieveCachedTheme(),
  );

  constructor() {
    this.activeTheme$ = this.supportedThemes$
      .asObservable()
      .pipe(map(theme => SupportedThemes[theme] || SupportedThemes[ThemeService.DEFAULT_THEME]));

    this.supportedThemes$.pipe(skip(1)).subscribe(theme => {
      this.cacheTheme(theme);
      this.setThemeHTMLData(theme);
    });
  }

  public switchTheme(theme: SupportedThemesT): void {
    this.supportedThemes$.next(theme);
  }

  private setThemeHTMLData(theme: SupportedThemesT): void {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private retrieveCachedTheme(): SupportedThemesT {
    return (
      <SupportedThemesT>localStorage.getItem(ThemeService.LS_KEY) || ThemeService.DEFAULT_THEME
    );
  }

  private cacheTheme(theme: SupportedThemesT): void {
    localStorage.setItem(ThemeService.LS_KEY, theme);
  }
}
